/**
 * Rules for which Call Notes form fields are visible, based on the selected call status.
 * Status values are normalized so API may send slugs, labels, or mixed casing.
 */

export function normalizeCallStatusSelectionForRules(rawSelectedValue) {
    return String(rawSelectedValue ?? "")
        .toLowerCase()
        .trim()
        .replace(/[-_]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

export function normalizeCallStatusSelectionCompact(rawSelectedValue) {
    return normalizeCallStatusSelectionForRules(rawSelectedValue).replace(/\s+/g, "");
}

/** “Not interested” (any common spelling / slug). */
export function isCallStatusNotInterested(rawSelectedValue) {
    const normalized = normalizeCallStatusSelectionForRules(rawSelectedValue);
    const compact = normalizeCallStatusSelectionCompact(rawSelectedValue);
    if (compact === "notinterested") return true;
    if (normalized.includes("not interested")) return true;
    return false;
}

/** Eligible call status (slug or label). */
export function isCallStatusEligible(rawSelectedValue) {
    const normalized = normalizeCallStatusSelectionForRules(rawSelectedValue);
    const compact = normalizeCallStatusSelectionCompact(rawSelectedValue);
    return normalized === "eligible" || compact === "eligible";
}

/** Eligible, Update, or RTR Internal — no schedule fields and no reason dropdown. */
export function isCallStatusEligibleUpdateOrRtrInternal(rawSelectedValue) {
    const normalized = normalizeCallStatusSelectionForRules(rawSelectedValue);
    const compact = normalizeCallStatusSelectionCompact(rawSelectedValue);
    if (normalized === "eligible") return true;
    if (normalized === "update") return true;
    if (normalized === "rtr internal" || compact === "rtrinternal") return true;
    return false;
}

/** Completed / complete call status (slug or label). */
export function isCallStatusCompleted(rawSelectedValue) {
    const normalized = normalizeCallStatusSelectionForRules(rawSelectedValue);
    const compact = normalizeCallStatusSelectionCompact(rawSelectedValue);
    if (compact === "completed" || compact === "complete") return true;
    if (normalized === "completed" || normalized === "complete") return true;
    return false;
}
/** Completed / complete call status (slug or label). */
export function isCallStatusRtrOrEligible(rawSelectedValue) {
    const normalized = normalizeCallStatusSelectionForRules(rawSelectedValue);
    const compact = normalizeCallStatusSelectionCompact(rawSelectedValue);
    if (
        [
          "rtr internal",
          "R.T.R. Internal",
          "eligible",
          "Eligible",
          "rtr internal"
        ].includes(compact)
      ) {
        return true;
      }

      if (
        [
          "rtr internal",
          "R.T.R. Internal",
          "eligible",
          "Eligible",
          "rtr internal"
        ].includes(normalized)
      ) {
        return true;
      }
    return false;
}

/** Show Drug Test and DOT Completion Date (single date-only field) when status is completed. */
export function shouldShowCallNotesDrugTestAndDotCompletionDateFields(rawSelectedValue) {
    return isCallStatusCompleted(rawSelectedValue);
}

/** Show schedule date and time (date + 24h time inputs) — hidden for completed, not interested, etc. */
export function shouldShowCallNotesScheduleDateAndTimeFields(rawSelectedValue) {
    if (!String(rawSelectedValue ?? "").trim()) return true;
    if (isCallStatusNotInterested(rawSelectedValue)) return false;
    if (isCallStatusEligibleUpdateOrRtrInternal(rawSelectedValue)) return false;
    if (isCallStatusCompleted(rawSelectedValue)) return false;
    const normalized = normalizeCallStatusSelectionForRules(rawSelectedValue);
    if (normalized === "existing fedex driver") return false;
    if (normalized === "phone number invalid") return false;
    if (normalized === "only update") return false;
    if (normalized === "ready for rtr ack") return false;
    return true;
}

/** Show the “Not interested” reason dropdown (Reasons master filter). */
export function shouldShowCallNotesNotInterestedReasonDropdown(rawSelectedValue) {
    return isCallStatusNotInterested(rawSelectedValue);
}

/**
 * Map rows from POST `master/Reasons/filter` to `<select>` options.
 * @param {unknown[]} reasonRows
 * @returns {{ value: string, label: string }[]}
 */
export function mapReasonMasterFilterRowsToSelectOptions(reasonRows) {
    return (Array.isArray(reasonRows) ? reasonRows : [])
        .map((row) => {
            const reasonRecordId = String(row?.id ?? row?._id ?? "").trim();
            const displayLabel =
                String(
                    row?.title ??
                        row?.name ??
                        row?.reason ??
                        row?.reasonName ??
                        row?.description ??
                        row?.label ??
                        row?.display ??
                        ""
                ).trim() || reasonRecordId;
            return { value: reasonRecordId, label: displayLabel };
        })
        .filter((option) => option.value);
}
